import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '../hooks/useQuery';
import { useAuth } from '../hooks/useAuth';
import { api, User } from '../lib/api';
import {
  SectionLabel, Card, CardHeader, Spinner, EmptyState, Badge, UserName, Avatar, initials,
} from '../components/ui';

interface TreeNode {
  user: User;
  children: TreeNode[];
}

/**
 * Build a tree from the flat user list using manager_jira_user_id.
 * Returns:
 *   - roots: users who have subordinates (the real hierarchy — rendered as trees)
 *   - orphans: users with NO manager AND NO one reporting to them (loose nodes)
 *
 * Bad data is handled defensively: self-manager links, circular references,
 * and managers pointing at users outside the visible set all degrade to
 * treating the user as a root rather than crashing or hiding them.
 */
function buildTree(users: User[]): { roots: TreeNode[]; orphans: TreeNode[] } {
  const byId = new Map<string, TreeNode>();
  for (const u of users) byId.set(u.jira_user_id, { user: u, children: [] });

  const childIds = new Set<string>();
  const rootsInHierarchy: TreeNode[] = [];

  for (const node of byId.values()) {
    const mgrId = node.user.manager_jira_user_id;
    // Self-manager, missing manager, or manager outside the visible set →
    // this node is a root. Checking !== self avoids the trivial self-cycle;
    // longer cycles are broken by the descent guard below.
    const isRoot = !mgrId || mgrId === node.user.jira_user_id || !byId.has(mgrId);
    if (isRoot) {
      rootsInHierarchy.push(node);
    } else {
      const parent = byId.get(mgrId)!;
      parent.children.push(node);
      childIds.add(node.user.jira_user_id);
    }
  }

  // Cycle guard: walk from every root depth-first; any node we don't reach
  // was pulled into a cycle. Promote the first unreached node in each
  // cycle to a root so its subtree still renders.
  const reached = new Set<string>();
  const stack: TreeNode[] = [...rootsInHierarchy];
  while (stack.length) {
    const n = stack.pop()!;
    if (reached.has(n.user.jira_user_id)) continue;
    reached.add(n.user.jira_user_id);
    for (const c of n.children) stack.push(c);
  }
  for (const node of byId.values()) {
    if (!reached.has(node.user.jira_user_id)) {
      // Break the cycle by treating this node as a root. Mark it reached
      // so its subtree doesn't get re-promoted.
      rootsInHierarchy.push(node);
      const restack: TreeNode[] = [node];
      while (restack.length) {
        const n = restack.pop()!;
        if (reached.has(n.user.jira_user_id)) continue;
        reached.add(n.user.jira_user_id);
        for (const c of n.children) restack.push(c);
      }
    }
  }

  // Sort each level: active first, then by role seniority, then alphabetical.
  const roleOrder: Record<string, number> = { EXEC: 0, DIRECTOR: 1, DM: 2, SA: 2, IC: 3 };
  const seen = new Set<string>();
  function sortLevel(nodes: TreeNode[]) {
    nodes.sort((a, b) => {
      if (a.user.is_active !== b.user.is_active) return a.user.is_active ? -1 : 1;
      const ra = roleOrder[a.user.role] ?? 9;
      const rb = roleOrder[b.user.role] ?? 9;
      if (ra !== rb) return ra - rb;
      return a.user.display_name.localeCompare(b.user.display_name);
    });
    for (const n of nodes) {
      if (seen.has(n.user.jira_user_id)) { n.children = []; continue; }
      seen.add(n.user.jira_user_id);
      sortLevel(n.children);
    }
  }
  sortLevel(rootsInHierarchy);

  // Split roots into "hierarchy roots" (have subordinates) and "orphans"
  // (no manager AND nobody reports to them). The orphans render off to the
  // side so the main tree stays readable.
  const roots: TreeNode[] = [];
  const orphans: TreeNode[] = [];
  for (const r of rootsInHierarchy) {
    if (r.children.length > 0) roots.push(r);
    else orphans.push(r);
  }
  return { roots, orphans };
}

export default function AdminOrgChartPage() {
  const { startImpersonation, realUser } = useAuth();
  const navigate = useNavigate();
  // /team/members respects the caller's scope: admins see everyone; directors
  // and execs see their subtree + self; ICs/DMs/SAs just see themselves. This
  // means the chart renders with the correct visibility without any extra
  // server work.
  const { data, isLoading, error } = useQuery(() => api.team.members());
  // Hide deactivated users from the chart entirely — they're not part of the
  // current reporting structure, even though /team/members includes them for
  // historical data views. Inactive users still surface elsewhere (Gantt
  // dropdowns, scorecard rollups with data, Users & roles page).
  const users = (data?.members ?? []).filter((u) => u.is_active);
  const { roots, orphans } = useMemo(() => buildTree(users), [users]);

  async function viewAs(u: User) {
    if (u.jira_user_id === realUser?.jira_user_id) {
      navigate('/scorecard');
      return;
    }
    await startImpersonation(u);
    navigate('/scorecard');
  }

  return (
    <>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 6 }}>
        <SectionLabel>Org chart</SectionLabel>
        <span style={{ fontSize: 11, color: 'var(--color-text-muted)' }}>
          {users.length} user{users.length === 1 ? '' : 's'} · click a node to view their scorecard
        </span>
      </div>

      <Card>
        <CardHeader>
          <span style={{ fontSize: 12, fontWeight: 500 }}>Reporting hierarchy</span>
          <span style={{ fontSize: 11, color: 'var(--color-text-muted)', marginLeft: 8 }}>
            drawn from manager_jira_user_id on each user
          </span>
        </CardHeader>
        {isLoading ? <Spinner /> : error ? <EmptyState message={error} /> : roots.length === 0 && orphans.length === 0 ? (
          <EmptyState message="No users found in your scope." />
        ) : (
          <div style={{ padding: '20px 16px', overflow: 'auto', maxHeight: 'calc(100vh - 220px)' }}>
            {/* Main hierarchy — each top-level root renders as its own tree,
                with multiple roots laid out side-by-side.
                `width: max-content` + `margin: 0 auto` centers the tree when
                it fits, and lets the outer `overflow: auto` scroll cleanly
                when it doesn't (a plain `justify-content: center` clips the
                left side of wider-than-viewport content — flexbox quirk). */}
            {roots.length > 0 && (
              <div style={{
                width: 'max-content', minWidth: '100%', margin: '0 auto',
                display: 'flex', flexDirection: 'row', alignItems: 'flex-start', gap: 48,
              }}>
                {roots.map((r) => (
                  <TreeBranch key={r.user.jira_user_id} node={r} onClick={viewAs} />
                ))}
              </div>
            )}
            {/* Orphans — users with no manager AND nobody reporting to them.
                Shown in a flat grid at the bottom so they don't clutter the
                main tree. Usually service accounts, contractors, or
                unassigned hires that need their manager field set. */}
            {orphans.length > 0 && (
              <div style={{ marginTop: roots.length > 0 ? 40 : 0 }}>
                <div style={{
                  fontSize: 11, color: 'var(--color-text-muted)', textTransform: 'uppercase',
                  letterSpacing: '.04em', marginBottom: 8,
                }}>
                  Unassigned ({orphans.length}) — no manager, no reports
                </div>
                <div style={{
                  display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 8,
                }}>
                  {orphans.map((o) => (
                    <NodeCard key={o.user.jira_user_id} user={o.user} onClick={() => viewAs(o.user)} />
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </Card>
    </>
  );
}

function TreeBranch({ node, onClick }: { node: TreeNode; onClick: (u: User) => void }) {
  const hasChildren = node.children.length > 0;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', position: 'relative' }}>
      <NodeCard user={node.user} onClick={() => onClick(node.user)} />
      {hasChildren && (
        <>
          {/* Vertical trunk from node to the children bar. */}
          <div style={{
            width: 1, height: 20, background: 'var(--color-border-strong)',
          }} />
          {/* Horizontal bar spanning the width of the children row. Positioned
              absolutely so it doesn't affect layout; sized to match the
              children container below. */}
          <div style={{
            display: 'flex', justifyContent: 'center', alignItems: 'flex-start',
            gap: 24, position: 'relative',
          }}>
            {/* Horizontal connector between children. Only meaningful for 2+. */}
            {node.children.length > 1 && (
              <div style={{
                position: 'absolute', top: 0, left: 0, right: 0,
                borderTop: '1px solid var(--color-border-strong)',
              }} />
            )}
            {node.children.map((c) => (
              <div
                key={c.user.jira_user_id}
                style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', position: 'relative', paddingTop: 20 }}
              >
                {/* Vertical stem from horizontal bar down to this child. */}
                <div style={{
                  position: 'absolute', top: 0, left: '50%',
                  width: 1, height: 20, background: 'var(--color-border-strong)',
                }} />
                <TreeBranch node={c} onClick={onClick} />
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

function NodeCard({ user, onClick }: { user: User; onClick: () => void }) {
  const roleColor: 'blue' | 'green' | 'amber' | 'gray' =
    user.role === 'EXEC' ? 'blue'
    : user.role === 'DIRECTOR' ? 'green'
    : user.role === 'DM' || user.role === 'SA' ? 'amber'
    : 'gray';
  return (
    <button
      onClick={onClick}
      title={`View ${user.display_name}'s scorecard`}
      style={{
        display: 'flex', alignItems: 'center', gap: 10,
        padding: '8px 14px',
        minWidth: 200, maxWidth: 260,
        background: 'var(--color-surface)',
        border: '0.5px solid var(--color-border-strong)',
        borderRadius: 'var(--radius-md)',
        boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
        cursor: 'pointer',
        textAlign: 'left',
        opacity: user.is_active ? 1 : 0.6,
        color: 'inherit',
      }}
    >
      <Avatar initials={initials(user.display_name)} size={32} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 12, fontWeight: 500, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          <UserName name={user.display_name} active={user.is_active} />
        </div>
        <div style={{ fontSize: 10, color: 'var(--color-text-muted)', display: 'flex', alignItems: 'center', gap: 4, marginTop: 2 }}>
          <Badge variant={roleColor}>{user.role}</Badge>
          {user.title && <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{user.title}</span>}
        </div>
      </div>
    </button>
  );
}
